import {
	originTracingType,
	originType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';
import AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import OriginTracer from '@atlassiansox/origin-tracing/origin-tracer';

import { isStudioV2 } from '@atlassian/atlassian-studio-routing/get-studio-version';

export type IdentityEnv = 'dev' | 'staging' | 'prod';
export type MappedEnv = 'local' | 'test' | 'production' | 'staging';

const product = 'atlassianStudio';

export const createStudioOriginTracer = (): OriginTracer => new OriginTracer({ product });

const getGasApiHost = (mappedEnv: MappedEnv) => {
	const currentDomain = window.location.hostname;

	switch (mappedEnv) {
		case 'staging':
		case 'production':
			return `${currentDomain}/gateway/api/gasv3/api/v1`;
		case 'test':
		case 'local':
			return 'atlassian-studio.stg-east.frontend.public.atl-paas.net/gateway/api/gasv3/api/v1';
		default:
			return 'atlassian-studio.stg-east.frontend.public.atl-paas.net/gateway/api/gasv3/api/v1';
	}
};

export const initAnalyticsClient = ({
	identityEnv,
	mappedEnv,
}: {
	identityEnv: IdentityEnv;
	mappedEnv: MappedEnv;
}): AnalyticsWebClient => {
	const client = new AnalyticsWebClient(
		{
			env: identityEnv,
			origin: originType.WEB,
			product,
			subproduct: originType.WEB,
			version: isStudioV2() ? 'v2' : 'v1',
		},
		{
			apiHost: getGasApiHost(mappedEnv),
			// https://hello.atlassian.net/wiki/spaces/policy/pages/3303839825/Onboarding+a+Product+to+the+Cookies+Library+atlassian+browser-storage-controls#Upgrade-Analytics-Client-version-and-include-disableCookiePersistence-variable
			disableCookiePersistence: true,
		},
	);

	client.setOriginTracingHandlers({
		[originTracingType.ATL_ORIGIN]: (encodedOrigin: string) => {
			const origin = OriginTracer.fromEncoded(encodedOrigin);

			return {
				originTracingAttributes: origin.toAnalyticsAttributes(),
				...(!origin.isMalformed() && { taskSessionId: origin.id }),
			};
		},
	});

	return client;
};

export enum UIActionSubject {
	PICKER = 'picker',
}

export enum Action {
	CLICKED = 'clicked',
	OPENED = 'opened',
	SEARCH = 'searched',
	PAGINATE = 'paginated',
	SELECTED = 'selected',
}

export type ActionSubject = string;

export const getTrackEventName = (actionSubject: ActionSubject, action: Action) =>
	`${actionSubject} ${action}`;

export const getUIEventName = (actionSubject: UIActionSubject, action: Action) =>
	`${actionSubject} ${action}`;
